<template>
  <div class="time-scale">
    <div
      class="current-tick"
      :style="{ transform: `translateY(${tickOffset}px)` }"
    ></div>
    <div
      v-for="(minutes, idx) in marks"
      :key="idx"
      class="time-mark"
      :class="tickType(minutes)"
      :style="{ height: `${getMarkHeight()}px` }"
    >
      <!-- Сам штрих: теперь он позиционируется справа -->
      <div class="tick"></div>

      <!-- Подпись времени выводим для каждого 15-минутного шага -->
      <span class="time-label">{{ formatTime(minutes) }}</span>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'TimeScale',
  props: {
    marks: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      currentMinutes: 0,
      timer: null,
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    cellInterval() {
      return this.user.cell_interval ?? 15;
    },
    tickOffset() {
      // Высота одного интервала в пикселях (32px за 15 минут)
      const pixelsPerMinute = 32 / this.cellInterval;
      return this.currentMinutes * pixelsPerMinute;
    }
  },
  mounted() {
    this.updateCurrentMinutes();
    this.timer = setInterval(this.updateCurrentMinutes, 60000);
  },
  beforeUnmount() {
    clearInterval(this.timer);
  },
  methods: {
    // Преобразуем минуты в строку “HH:MM”
    formatTime(totalMinutes) {
      const h = Math.floor(totalMinutes / 60)
      const m = totalMinutes % 60
      const hh = String(h).padStart(2, '0')
      const mm = String(m).padStart(2, '0')
      return `${hh}:${mm}`
    },
    // Определяем тип штриха (“hour”, “half” или “quarter”)
    tickType(totalMinutes) {
      if (totalMinutes % 60 === 0) {
        return 'mark-hour'
      } else if (totalMinutes % 30 === 0) {
        return 'mark-half'
      } else {
        return 'mark-quarter'
      }
    },
    getMarkHeight() {
      return 32 * (this.cellInterval / 15);
    },
    updateCurrentMinutes() {
      const now = new Date();
      this.currentMinutes = now.getHours() * 60 + now.getMinutes();
    }
  }
}
</script>

<style scoped>
/* Основной контейнер: ширина 80px, фон и граница */
.time-scale {
  width: 150px;
  background: transparent;
  box-sizing: border-box;
  margin-top: 0;
  padding-bottom: 0;
  padding-left: 8px;
  padding-right: 8px;
  border-radius: 10px 10px 0 0;
  overflow: hidden;
}

/* Каждый «рядышком» блок занимает 32px высоты */
.time-mark {
  position: relative;
  height: 32px; /* 15 минут = 32px */
  display: flex;
  align-items: center; /* вертикальное выравнивание контента внутри */
  justify-content: center;
  background: var(--cal-bg-cell-alt);
}

.time-mark:nth-child(even) {
  background: var(--cal-bg-cell);
}

.time-mark:first-of-type {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

/* ------------ ШТРИХИ (TICK) ------------ */

/* Общие стили для штриха: абсолютное позиционирование справа */
.tick {
  display: none;
  position: absolute;
  right: 0;
  /* высота 2px или 1px задаём в конкретных классах */
  background-color: transparent;
}

/* ЧАСОВОЙ штрих (например, 09:00): ширина 32px, толщина 2px, чёрный */
.mark-hour .tick {
  width: 32px;
  height: 2px;
  background-color: var(--cal-accent);
}

/* ПОЛУчасовой штрих (например, 09:30): ширина 24px, толщина 1px, темно-серый */
.mark-half .tick {
  width: 24px;
  height: 1px;
  background-color: var(--cal-border);
}

/* ЧЕТВЕРТНОЙ штрих (например, 09:15/09:45): ширина 16px, толщина 1px, светло-серый */
.mark-quarter .tick {
  width: 16px;
  height: 1px;
  background-color: rgba(124, 157, 211, 0.45);
}

/* ------------ ПОДПИСИ (TIME LABEL) ------------ */

/* Общие стили для подписи времени */
.time-label {
  position: absolute;
  right: 0;
  left: 0;
  text-align: center;
  white-space: nowrap;
}

/* Часовые метки */
.mark-hour .time-label {
  font-size: 15px;
  font-weight: 700;
  color: var(--cal-text-primary);
  line-height: 32px; /* чтобы выравнять подпись по вертикали */
  letter-spacing: 0.2px;
  text-shadow: none;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
}

/* Получасовые метки (меньше часовых) */
.mark-half .time-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--cal-text-primary);
  line-height: 32px;
  letter-spacing: 0.2px;
  text-shadow: none;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
}

/* ЧЕТВЕРТ (09:15/09:45): самый мелкий, тонкий, бледно-серый */
.mark-quarter .time-label {
  font-size: 12px;
  font-weight: 400;
  color: var(--cal-text-regular);
  line-height: 32px;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
}
</style>

.current-tick {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background-color: var(--cal-now-line);
  z-index: 10;
  transition: transform 0.3s linear;
}
